/* eslint-disable i18next/no-literal-string */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/api';
import { Questionnaire, StudyConfig, StudyQuestion, StudySession } from '../types/study';
import { YouTubePlayer } from '../components/shared/YouTubePlayer';
import { classifyVideoSource } from '../utils/videoSources';
import './study.css';
import { STUDY_COPY as COPY } from './studyCopy';
import { resolveBackendResourceUrl } from '../config/environment';

type Step = 'consent' | 'demographics' | 'video' | 'questions' | 'complete' | 'declined' | 'withdrawn';
type Answer = string | number | boolean | string[];
const QUESTION_CATEGORIES = ['critical', 'memory', 'false_memory', 'other'];
function invalidDraft(question: StudyQuestion, value: Answer | undefined) {
  const missing = value === undefined || (typeof value === 'string' && !value.trim()) || (Array.isArray(value) && !value.length);
  if (missing) return question.required;
  if (question.type === 'number') return typeof value !== 'number' || !Number.isFinite(value)
    || (question.minimum != null && value < question.minimum) || (question.maximum != null && value > question.maximum);
  if (question.type === 'boolean') return typeof value !== 'boolean';
  if (question.type === 'single_choice') return !question.options?.includes(String(value));
  if (question.type === 'multiple_choice') return !Array.isArray(value) || value.some((item) => !question.options?.includes(item));
  return typeof value !== 'string';
}



const STORAGE_KEY = 'loftus-palmer-session';
const CREATE_REQUEST_KEY = 'loftus-palmer-create-request';
const PILOT_VIDEO_URL = '/media/pilot-accident.webm';

function normalizeSessionVideo(session: StudySession): StudySession {
  if (session.video.source_type !== 'uploaded') return session;
  return { ...session, video: { ...session.video, url: resolveBackendResourceUrl(session.video.url) } };
}

function newRequestId() {
  if (typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function apiError(error: unknown, fallback: string) {
  const candidate = error as { response?: { data?: { detail?: string } } };
  return candidate.response?.data?.detail || fallback;
}

function StudyHeader({ step }: { step: Step }) {
  const current = step === 'consent' || step === 'demographics' ? 0 : step === 'video' ? 1 : step === 'questions' ? 2 : null;
  const completedThrough = step === 'complete' ? 2 : current ?? -1;
  const labels = [COPY.stepConsent, COPY.stepVideo, COPY.stepQuestions];
  return (
    <header className="gp-header">
      <div className="gp-header__inner">
        <div className="gp-brand">
          <p className="gp-eyebrow">{COPY.eyebrow}</p>
          <strong className="gp-mark">{COPY.mark}</strong>
        </div>
        <nav className="gp-progress" aria-label="Progreso del protocolo">
          {labels.map((label, index) => (
            <span key={label} className={index <= completedThrough ? 'is-active' : ''} aria-current={index === current ? 'step' : undefined}>
              <b>{index + 1}</b>{label}
            </span>
          ))}
        </nav>
        <Link className="gp-research-link" to="/login">{COPY.researcherAccess}</Link>
      </div>
    </header>
  );
}

const StudyPage: React.FC = () => {
  const [config, setConfig] = useState<StudyConfig | null>(null);
  const [session, setSession] = useState<StudySession | null>(null);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [step, setStep] = useState<Step>('consent');
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [errorFieldId, setErrorFieldId] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [program, setProgram] = useState('');
  const [answers, setAnswers] = useState<Record<number, Answer>>({});
  const [questionStage, setQuestionStage] = useState(0);
  const [watched, setWatched] = useState(0);
  const [videoVerified, setVideoVerified] = useState(false);
  const [withdrawConfirm, setWithdrawConfirm] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const playbackStarted = useRef(false);
  const furthestPosition = useRef(0);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const errorAlertRef = useRef<HTMLElement | null>(null);

  const bindErrorRef = (node: HTMLElement | null) => { errorAlertRef.current = node; };
  const showError = (message: string, fieldId = '') => {
    setError(message);
    setErrorFieldId(fieldId);
    window.setTimeout(() => errorAlertRef.current?.focus(), 0);
  };

  const loadConfig = async () => {
    setLoading(true);
    setError('');
    setErrorFieldId('');
    try {
      const response = await apiService.getStudyConfig();
      setConfig(response.data);
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        let local: { session?: StudySession; step?: Step; answers?: Record<number, Answer>; questionStage?: number } | null = null;
        try {
          local = JSON.parse(stored) as { session?: StudySession; step?: Step; answers?: Record<number, Answer>; questionStage?: number };
        } catch {
          // Browser storage is not trusted input. A stale/corrupt entry must
          // not turn an otherwise available study into a permanent load error.
          sessionStorage.removeItem(STORAGE_KEY);
        }
        if (!local) return;
        if (!local.session?.token) {
          sessionStorage.removeItem(STORAGE_KEY);
        } else {
          try {
            const resumed = (await apiService.getStudySession(local.session.token)).data;
            setSession(normalizeSessionVideo(resumed));
            const draft = local.answers && typeof local.answers === 'object' && !Array.isArray(local.answers) ? local.answers : {};
            setAnswers(draft);
            const savedStage = Number.isInteger(local.questionStage) ? Math.max(0, local.questionStage!) : 0;
            const groups = QUESTION_CATEGORIES.map((category) => (resumed.questionnaire?.questions || []).filter((q) => (q.category || 'other') === category)).filter((group) => group.length);
            const incompleteStage = groups.findIndex((group) => group.some((q) => invalidDraft(q, draft[q.id])));
            setQuestionStage(incompleteStage < 0 ? savedStage : Math.min(savedStage, incompleteStage));
            if (resumed.questionnaire) {
              setQuestionnaire(resumed.questionnaire);
              setVideoVerified(true);
            }
            const resumedStep: Step = resumed.status === 'completed'
              ? 'complete'
              : resumed.status === 'withdrawn'
                ? 'withdrawn'
              : resumed.questionnaire && local.step === 'questions'
                ? 'questions'
                : 'video';
            setStep(resumedStep);
            persistStoredSession(resumedStep, resumed, resumed.questionnaire || null);
          } catch (resumeError) {
            const status = (resumeError as { response?: { status?: number } }).response?.status;
            if (status !== 404) throw resumeError;
            sessionStorage.removeItem(STORAGE_KEY);
          }
        }
      }
    } catch (requestError) {
      setConfig(null);
      showError(apiError(requestError, COPY.loadError));
    } finally {
      setLoading(false);
    }
  };

  // Session recovery is intentionally evaluated once when this route mounts;
  // explicit retry actions invoke the same loader directly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadConfig(); }, []);

  useEffect(() => {
    if (loading || !config?.available) return undefined;
    const timeout = window.setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      stepHeadingRef.current?.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [config, loading, step, questionStage]);

  const persistStoredSession = (nextStep: Step, nextSession: StudySession, nextQuestionnaire: Questionnaire | null) => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ session: nextSession, questionnaire: nextQuestionnaire, step: nextStep }));
  };

  const persist = (nextStep: Step, nextSession = session, nextQuestionnaire = questionnaire) => {
    if (nextSession) persistStoredSession(nextStep, nextSession, nextQuestionnaire);
  };

  const createSession = async (event: React.FormEvent) => {
    event.preventDefault();
    const parsedAge = Number(age);
    if (!Number.isInteger(parsedAge) || parsedAge < 18 || parsedAge > 120) {
      showError(COPY.ageError, 'age');
      return;
    }
    setPending(true);
    setError('');
    setErrorFieldId('');
    try {
      const clientRequestId = sessionStorage.getItem(CREATE_REQUEST_KEY) || newRequestId();
      sessionStorage.setItem(CREATE_REQUEST_KEY, clientRequestId);
      const response = await apiService.createStudySession({
        client_request_id: clientRequestId,
        consent_accepted: true,
        age: parsedAge,
        ...(gender ? { gender } : {}),
        ...(program.trim() ? { academic_program: program.trim() } : {}),
      });
      setSession(normalizeSessionVideo(response.data));
      sessionStorage.removeItem(CREATE_REQUEST_KEY);
      setStep('video');
      persist('video', response.data, null);
    } catch (requestError) {
      showError(apiError(requestError, COPY.sessionError), 'demographics-form');
    } finally {
      setPending(false);
    }
  };

  const withdraw = async () => {
    if (!session || withdrawing || pending) return;
    setWithdrawing(true);
    setError('');
    setErrorFieldId('');
    try {
      await apiService.withdrawStudySession(session.token);
      sessionStorage.removeItem(STORAGE_KEY);
      setWithdrawConfirm(false);
      setStep('withdrawn');
    } catch (requestError) {
      showError(apiError(requestError, COPY.withdrawError), 'withdrawal-controls');
    } finally {
      setWithdrawing(false);
    }
  };

  const renderWithdrawal = () => (
    <div className="gp-withdrawal" id="withdrawal-controls">
      {!withdrawConfirm ? (
        <button className="gp-button gp-button--quiet" type="button" onClick={() => setWithdrawConfirm(true)} disabled={pending}>{COPY.withdraw}</button>
      ) : (
        <div role="group" aria-label={COPY.withdraw}>
          <p>{COPY.withdrawPrompt}</p>
          <div className="gp-actions">
            <button data-testid="withdraw-confirm" className="gp-button gp-button--primary" type="button" onClick={withdraw} disabled={withdrawing || pending}>{withdrawing ? COPY.withdrawing : COPY.withdrawConfirm}</button>
            <button className="gp-button gp-button--quiet" type="button" onClick={() => setWithdrawConfirm(false)} disabled={withdrawing}>{COPY.withdrawCancel}</button>
          </div>
        </div>
      )}
      {error && errorFieldId === 'withdrawal-controls' && <p className="gp-error" role="alert" tabIndex={-1} ref={bindErrorRef}>{error}</p>}
    </div>
  );

  const startPlayback = async () => {
    if (!session || playbackStarted.current) return;
    playbackStarted.current = true;
    setError('');
    setErrorFieldId('');
    try {
      await apiService.startPlayback(session.token);
    } catch (requestError) {
      playbackStarted.current = false;
      showError(apiError(requestError, COPY.videoError), session?.video.source_type === 'youtube' ? 'experiment-youtube' : 'experiment-video');
    }
  };

  const completePlayback = async (endedPosition: number, detectedDuration: number) => {
    if (!session || pending || videoVerified) return;
    setPending(true);
    setError('');
    setErrorFieldId('');
    try {
      const duration = Number.isFinite(detectedDuration) ? detectedDuration : session.video.duration_seconds;
      const response = await apiService.completePlayback(session.token, endedPosition, duration);
      setQuestionnaire(response.data.questionnaire);
      setVideoVerified(true);
      persist('video', session, response.data.questionnaire);
    } catch (requestError) {
      showError(apiError(requestError, COPY.videoError), session.video.source_type === 'youtube' ? 'experiment-youtube' : 'experiment-video');
    } finally {
      setPending(false);
    }
  };

  const questions = useMemo(() => questionnaire?.questions || [], [questionnaire]);
  const questionStages = useMemo(() => [
    { title: 'Pregunta crítica', questions: questions.filter((q) => q.category === 'critical') },
    { title: 'Preguntas de memoria', questions: questions.filter((q) => q.category === 'memory') },
    { title: 'Preguntas de falsas memorias', questions: questions.filter((q) => q.category === 'false_memory') },
    { title: 'Preguntas adicionales', questions: questions.filter((q) => !['critical', 'memory', 'false_memory'].includes(q.category || 'other')) },
  ].filter((stage) => stage.questions.length), [questions]);
  const currentStageIndex = Math.min(Math.max(0, questionStage), Math.max(0, questionStages.length - 1));
  const currentStage = questionStages[currentStageIndex];
  const stageQuestions = currentStage?.questions || [];
  const isFinalStage = currentStageIndex === questionStages.length - 1;

  useEffect(() => {
    if (step === 'questions' && session) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ session, step, answers, questionStage: currentStageIndex }));
    }
  }, [step, session, answers, currentStageIndex]);

  const firstMissingRequired = stageQuestions.find((question) => {
    const value = answers[question.id];
    return question.required && (
      value === undefined
      || (typeof value === 'string' && value.trim() === '')
      || (Array.isArray(value) && value.length === 0)
    );
  });

  const submitResponses = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!session || firstMissingRequired) {
      showError(COPY.answerError, firstMissingRequired ? `question-${firstMissingRequired.id}` : 'questionnaire-form');
      return;
    }
    const invalidNumber = stageQuestions.find((q) => q.type === 'number' && answers[q.id] !== undefined && answers[q.id] !== '' && (
      !Number.isFinite(Number(answers[q.id]))
      || (q.minimum != null && Number(answers[q.id]) < q.minimum)
      || (q.maximum != null && Number(answers[q.id]) > q.maximum)
    ));
    if (invalidNumber) {
      showError('Ingresa un número dentro del intervalo indicado.', `question-${invalidNumber.id}`);
      return;
    }
    if (!isFinalStage) {
      setError('');
      setErrorFieldId('');
      setQuestionStage(currentStageIndex + 1);
      return;
    }
    setPending(true);
    setError('');
    setErrorFieldId('');
    try {
      await apiService.submitStudyResponses(session.token, questions
        .filter((question) => answers[question.id] !== undefined && answers[question.id] !== '')
        .map((question) => ({ question_id: question.id, value: answers[question.id] })));
      setStep('complete');
      persist('complete');
    } catch (requestError) {
      const fields = (requestError as { response?: { data?: { fields?: Record<string, string> } } }).response?.data?.fields || {};
      const failedStage = questionStages.findIndex((group) => group.questions.some((q) => fields[String(q.id)]));
      if (failedStage >= 0) {
        setQuestionStage(failedStage);
        const failedQuestion = questionStages[failedStage].questions.find((q) => fields[String(q.id)]);
        showError(apiError(requestError, COPY.submitError), `question-${failedQuestion!.id}`);
      } else {
        showError(apiError(requestError, COPY.submitError), 'questionnaire-form');
      }
    } finally {
      setPending(false);
    }
  };

  const renderQuestion = (question: StudyQuestion, index: number) => {
    const inputId = `question-${question.id}`;
    const value = answers[question.id] ?? '';
    const invalid = errorFieldId === inputId;
    const update = (nextValue: Answer) => {
      setAnswers((current) => ({ ...current, [question.id]: nextValue }));
      if (invalid) { setError(''); setErrorFieldId(''); }
    };
    const label = `${index + 1}. ${question.prompt}`;
    return (
      <fieldset className="gp-question" key={question.id} data-question-id={question.id} aria-describedby={invalid ? 'questions-error' : undefined}>
        <legend>{label} {question.required && <span className="gp-required">{COPY.required}</span>}</legend>
        {question.type === 'single_choice' && (
          <select data-question-id={question.id} data-testid={`question-${question.id}`} id={inputId} value={String(value)} onChange={(event) => update(event.target.value)} required={question.required} aria-invalid={invalid} aria-describedby={invalid ? 'questions-error' : undefined}>
            <option value="">{COPY.select}</option>
            {(question.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        )}
        {question.type === 'boolean' && (
          <div className="gp-choice-row" data-question-id={question.id} data-testid={`question-${question.id}`}>
            {[COPY.yes, COPY.no].map((option) => (
              <label key={option}><input type="radio" name={inputId} checked={value === (option === COPY.yes)} onChange={() => update(option === COPY.yes)} aria-describedby={invalid ? 'questions-error' : undefined} />{option}</label>
            ))}
          </div>
        )}
        {question.type === 'text' && <textarea data-question-id={question.id} data-testid={`question-${question.id}`} id={inputId} value={String(value)} onChange={(event) => update(event.target.value)} rows={3} required={question.required} aria-invalid={invalid} aria-describedby={invalid ? 'questions-error' : undefined} />}
        {question.type === 'multiple_choice' && <div className="gp-choice-row gp-choice-row--wrap">{(question.options || []).map((option) => { const selected = Array.isArray(value) ? value : []; return <label key={option}><input type="checkbox" checked={selected.includes(option)} onChange={(event) => update(event.target.checked ? [...selected, option] : selected.filter((item) => item !== option))} aria-describedby={invalid ? 'questions-error' : undefined} />{option}</label>; })}</div>}
        {question.type === 'number' && (
          <div className="gp-number-wrap">
            <input data-question-id={question.id} data-testid={question.key === 'critical_speed' || question.category === 'critical' ? 'critical-speed' : `question-${question.id}`} id={inputId} type="number" min={question.minimum ?? undefined} max={question.maximum ?? undefined} step="any" inputMode="decimal" value={String(value)} onChange={(event) => update(event.target.value === '' ? '' : Number(event.target.value))} required={question.required} aria-invalid={invalid} aria-describedby={invalid ? 'questions-error' : undefined} />
            {(question.key === 'critical_speed' || question.category === 'critical') && <span>{questionnaire?.speed_unit}</span>}
          </div>
        )}
      </fieldset>
    );
  };

  const classifiedVideo = session ? classifyVideoSource(session.video.url) : null;
  const youtubeVideoId = session?.video.youtube_video_id
    || (classifiedVideo?.kind === 'youtube' ? classifiedVideo.videoId : null);

  if (loading) return <main className="gp-state" data-testid="study-loading" role="status"><span className="gp-spinner" />{COPY.loading}</main>;
  if (error && !config) return <main className="gp-state" data-testid="study-error" role="alert" tabIndex={-1} ref={bindErrorRef}><h1>{COPY.loadError}</h1><button onClick={loadConfig}>{COPY.retry}</button></main>;
  if (!config?.available || !config.study.video.configured) return <main className="gp-state gp-state--blocked" data-testid="study-blocked"><p className="gp-eyebrow">{COPY.eyebrow}</p><h1>{COPY.blockedTitle}</h1><p>{config?.blocked_reason || COPY.blockedBody}</p><Link to="/login">{COPY.researcherAccess}</Link></main>;

  return (
    <div className="gp-shell">
      <StudyHeader step={step} />
      <main className="gp-main">
        {step === 'consent' && (
          <section className="gp-protocol" data-testid="consent-panel">
            <aside className="gp-context"><span>01</span><p>{COPY.voluntary}</p><h2>{config.study.title}</h2><p>{config.study.description}</p><dl><div><dt>{COPY.duration}</dt><dd>≈ {Math.ceil((config.study.video.duration_seconds || 0) / 60) + 5} min</dd></div><div><dt>{COPY.registry}</dt><dd>{COPY.anonymous}</dd></div></dl></aside>
            <article className="gp-document"><p className="gp-eyebrow">Versión {config.study.consent.version}</p><h1 className="gp-step-title" tabIndex={-1} ref={stepHeadingRef}>{config.study.consent.title}</h1><div className="gp-consent-copy">{config.study.consent.body}</div><div className="gp-note"><strong>{COPY.voluntary}</strong><p>{config.study.consent.withdrawal_text}</p></div><p className="gp-privacy">{COPY.privacy}</p><div className="gp-actions"><button className="gp-button gp-button--primary" onClick={() => setStep('demographics')}>{COPY.consentAccept}</button><button className="gp-button gp-button--quiet" onClick={() => setStep('declined')}>{COPY.consentDecline}</button></div></article>
          </section>
        )}

        {step === 'demographics' && (
          <section className="gp-card gp-card--narrow">
            <p className="gp-eyebrow">{COPY.sampleData}</p><h1 className="gp-step-title" tabIndex={-1} ref={stepHeadingRef}>{COPY.demographicsTitle}</h1><p>{COPY.demographicsBody}</p>
            <form data-testid="demographics-form" onSubmit={createSession} noValidate aria-describedby={error && errorFieldId === 'demographics-form' ? 'demographics-error' : undefined}>
              <label htmlFor="age">{COPY.age}<span aria-hidden="true"> *</span></label>
              <input id="age" name="age" type="number" min="18" max="120" inputMode="numeric" value={age} onChange={(event) => { setAge(event.target.value); if (errorFieldId === 'age') { setError(''); setErrorFieldId(''); } }} aria-describedby={errorFieldId === 'age' ? 'age-hint demographics-error' : 'age-hint'} aria-invalid={errorFieldId === 'age'} required />
              <small id="age-hint">{COPY.ageHint}</small>
              <label htmlFor="gender">{COPY.gender}</label>
              <select id="gender" name="gender" value={gender} onChange={(event) => setGender(event.target.value)}><option value="">{COPY.genderPlaceholder}</option><option value="mujer">{COPY.woman}</option><option value="hombre">{COPY.man}</option><option value="no-binario">{COPY.nonBinary}</option><option value="otro">{COPY.other}</option></select>
              <label htmlFor="program">{COPY.program}</label>
              <input id="program" name="academic_program" value={program} onChange={(event) => setProgram(event.target.value)} placeholder={COPY.programPlaceholder} />
              {error && <p id="demographics-error" className="gp-error" role="alert" tabIndex={-1} ref={bindErrorRef}>{error}</p>}
              <button className="gp-button gp-button--primary" type="submit" disabled={pending}>{pending ? COPY.creating : COPY.continueVideo}</button>
            </form>
          </section>
        )}

        {step === 'video' && session && (
          <section className="gp-video-stage" data-testid="video-stage"><div className="gp-video-copy"><p className="gp-eyebrow">Sesión {session.participant_code}</p><h1 className="gp-step-title" tabIndex={-1} ref={stepHeadingRef}>{COPY.videoTitle}</h1><p id="video-instructions">{COPY.videoBody}</p>{session.video.url === PILOT_VIDEO_URL && <aside className="gp-pilot-media" id="pilot-media-provenance" data-testid="pilot-media-provenance"><strong>{COPY.pilotMedia}</strong><span>{COPY.pilotProvenance}</span></aside>}<div className="gp-meter" role="progressbar" aria-label={COPY.videoProgress} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(Math.min(100, watched))} aria-valuetext={videoVerified ? COPY.videoReady : `${Math.round(watched)}%`}><span style={{ width: `${Math.min(100, watched)}%` }} /></div><p aria-live="polite">{pending ? COPY.videoPending : videoVerified ? COPY.videoReady : `${Math.round(watched)}%`}</p>{error && errorFieldId !== 'withdrawal-controls' && <p id="video-error" className="gp-error" role="alert" tabIndex={-1} ref={bindErrorRef}>{error}</p>}<button data-testid="video-complete" className="gp-button gp-button--primary" disabled={!videoVerified || pending} aria-describedby={!videoVerified ? 'video-continue-blocked' : undefined} onClick={() => { setStep('questions'); persist('questions'); }}>{COPY.continueQuestions}</button>{!videoVerified && <p id="video-continue-blocked" className="gp-help">{COPY.videoContinueBlocked}</p>}{renderWithdrawal()}</div><div className="gp-video-frame">{youtubeVideoId ? <YouTubePlayer videoId={youtubeVideoId} ariaLabel={COPY.videoLabel} className="gp-youtube-player" testId="experiment-youtube" onPlay={startPlayback} onProgress={(currentTime, duration) => setWatched(duration ? (currentTime / duration) * 100 : 0)} onEnded={(currentTime, duration) => { void completePlayback(currentTime, duration); }} onError={message => showError(message, 'experiment-youtube')} /> : <video data-testid="experiment-video" src={session.video.url} controls controlsList="nodownload noplaybackrate" disablePictureInPicture playsInline preload="metadata" aria-label={COPY.videoLabel} aria-describedby={`${errorFieldId === 'experiment-video' ? 'video-instructions video-error' : 'video-instructions'}${session.video.url === PILOT_VIDEO_URL ? ' pilot-media-provenance' : ''}`} onPlay={startPlayback} onError={() => showError(COPY.videoSourceError, 'experiment-video')} onTimeUpdate={(event) => { const video = event.currentTarget; furthestPosition.current = Math.max(furthestPosition.current, video.currentTime); setWatched(video.duration ? (video.currentTime / video.duration) * 100 : 0); }} onSeeking={(event) => { const video = event.currentTarget; if (video.currentTime > furthestPosition.current + 1) video.currentTime = furthestPosition.current; }} onEnded={(event) => { void completePlayback(event.currentTarget.currentTime, event.currentTarget.duration); }}>{COPY.videoFallback}</video>}</div></section>
        )}

        {step === 'questions' && questionnaire && (
          <section className="gp-card gp-card--questions"><p className="gp-eyebrow">Sesión {session?.participant_code}</p><h1 className="gp-step-title" tabIndex={-1} ref={stepHeadingRef}>{currentStage?.title}</h1><p aria-live="polite">Sección {currentStageIndex + 1} de {questionStages.length}</p><p>{COPY.questionsBody}</p><form data-testid="questionnaire-form" onSubmit={submitResponses} noValidate aria-describedby={error && errorFieldId === 'questionnaire-form' ? 'questions-error' : undefined}>{stageQuestions.map(renderQuestion)}{error && errorFieldId !== 'withdrawal-controls' && <p id="questions-error" className="gp-error" role="alert" tabIndex={-1} ref={bindErrorRef}>{error}</p>}<button data-testid={isFinalStage ? 'submit-responses' : 'continue-question-stage'} className="gp-button gp-button--primary" type="submit" disabled={pending || withdrawing}>{pending ? COPY.submitting : isFinalStage ? COPY.submit : 'Continuar'}</button></form>{renderWithdrawal()}
          </section>
        )}

        {step === 'complete' && (
          <section className="gp-state gp-state--success" data-testid="study-complete"><span className="gp-success-mark">✓</span><p className="gp-eyebrow">{COPY.thanks}</p><h1 className="gp-step-title" tabIndex={-1} ref={stepHeadingRef}>{COPY.completeTitle}</h1><p>{COPY.completeBody}</p><div className="gp-code"><span>{COPY.anonymousCode}</span><strong>{session?.participant_code}</strong></div></section>
        )}

        {step === 'declined' && (
          <section className="gp-state" data-testid="study-declined"><p className="gp-eyebrow">{COPY.voluntary}</p><h1 className="gp-step-title" tabIndex={-1} ref={stepHeadingRef}>{COPY.declinedTitle}</h1><p>{COPY.declinedBody}</p><button className="gp-button gp-button--quiet" onClick={() => setStep('consent')}>{COPY.returnStart}</button></section>
        )}

        {step === 'withdrawn' && (
          <section className="gp-state" data-testid="study-withdrawn"><p className="gp-eyebrow">{COPY.voluntary}</p><h1 className="gp-step-title" tabIndex={-1} ref={stepHeadingRef}>{COPY.withdrawnTitle}</h1><p>{COPY.withdrawnBody}</p><div className="gp-code"><span>{COPY.anonymousCode}</span><strong>{session?.participant_code}</strong></div></section>
        )}
      </main>
      <footer className="gp-footer"><span>{COPY.protocol}</span><span>{COPY.footer}</span></footer>
    </div>
  );
};

export default StudyPage;
